/**
 * MyTasksList - Task list grouped by status for Participant Workspace
 *
 * Groups tasks into:
 * - Ready (can start now)
 * - Waiting (blocked by dependencies)
 * - Completed
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { colors } from '../../theme/colors';

interface StepBlockedInfo {
  isBlocked: boolean;
  hiddenPrereqCount: number;
  visiblePrereqIds: string[];
  blockedReason: string | null;
  canStart: boolean;
}

interface ParticipantStep {
  stepId: string;
  title: string;
  description: string | null;
  status: string;
  phaseLabel: string | null;
  blocked: StepBlockedInfo;
  assignedViaRole: string | null;
}

interface MyTasksListProps {
  steps: ParticipantStep[];
  selectedStepId: string | null;
  onStepSelect: (stepId: string) => void;
}

export const MyTasksList: React.FC<MyTasksListProps> = ({
  steps,
  selectedStepId,
  onStepSelect,
}) => {
  const [showCompleted, setShowCompleted] = React.useState(false);

  // Group steps by status
  const readySteps = steps.filter(
    (s) => s.status !== 'COMPLETED' && s.blocked.canStart
  );
  const waitingSteps = steps.filter(
    (s) => s.status !== 'COMPLETED' && !s.blocked.canStart
  );
  const completedSteps = steps.filter((s) => s.status === 'COMPLETED');

  const renderStep = (step: ParticipantStep, showStatus: 'ready' | 'waiting' | 'completed') => {
    const isSelected = step.stepId === selectedStepId;
    const isCompleted = step.status === 'COMPLETED';

    return (
      <Paper
        key={step.stepId}
        elevation={0}
        onClick={() => onStepSelect(step.stepId)}
        sx={{
          p: 2,
          mb: 1,
          cursor: 'pointer',
          border: `1px solid ${isSelected ? colors.blue[400] : colors.border}`,
          borderRadius: 2,
          bgcolor: isSelected ? colors.blue[50] : colors.card,
          transition: 'all 0.15s ease',
          '&:hover': {
            borderColor: colors.blue[300],
            bgcolor: isSelected ? colors.blue[50] : colors.gray[50],
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {/* Status icon */}
          <Box sx={{ mt: 0.25 }}>
            {isCompleted ? (
              <CheckCircleIcon sx={{ fontSize: 20, color: colors.green[600] }} />
            ) : showStatus === 'ready' ? (
              <PlayArrowIcon sx={{ fontSize: 20, color: colors.blue[500] }} />
            ) : (
              <HourglassEmptyIcon sx={{ fontSize: 20, color: colors.amber[500] }} />
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: isCompleted ? colors.textSecondary : colors.textPrimary,
                textDecoration: isCompleted ? 'line-through' : 'none',
                mb: 0.5,
              }}
            >
              {step.title}
            </Typography>

            {/* Phase label and role */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {step.phaseLabel && (
                <Typography
                  variant="caption"
                  sx={{ color: colors.textSecondary }}
                >
                  {step.phaseLabel}
                </Typography>
              )}
              {step.assignedViaRole && (
                <Chip
                  label={step.assignedViaRole}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    bgcolor: colors.purple[50],
                    color: colors.purple[600],
                  }}
                />
              )}
            </Box>

            {/* Blocked reason */}
            {showStatus === 'waiting' && step.blocked.blockedReason && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  color: colors.amber[700],
                  fontStyle: 'italic',
                }}
              >
                {step.blocked.blockedReason}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      {/* Ready section */}
      {readySteps.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <PlayArrowIcon sx={{ fontSize: 18, color: colors.blue[500] }} />
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
              Ready to Start
            </Typography>
            <Chip
              label={readySteps.length}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: colors.blue[50],
                color: colors.blue[600],
              }}
            />
          </Box>
          {readySteps.map((step) => renderStep(step, 'ready'))}
        </Box>
      )}

      {/* Waiting section */}
      {waitingSteps.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <HourglassEmptyIcon sx={{ fontSize: 18, color: colors.amber[500] }} />
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
              Waiting
            </Typography>
            <Chip
              label={waitingSteps.length}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: colors.amber[50],
                color: colors.amber[700],
              }}
            />
          </Box>
          {waitingSteps.map((step) => renderStep(step, 'waiting'))}
        </Box>
      )}

      {/* Completed section - collapsible */}
      {completedSteps.length > 0 && (
        <Box>
          <Box
            onClick={() => setShowCompleted(!showCompleted)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 18, color: colors.green[600] }} />
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
              Completed
            </Typography>
            <Chip
              label={completedSteps.length}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: colors.green[50],
                color: colors.green[600],
              }}
            />
            <IconButton size="small" sx={{ ml: 'auto' }}>
              {showCompleted ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
          <Collapse in={showCompleted}>
            {completedSteps.map((step) => renderStep(step, 'completed'))}
          </Collapse>
        </Box>
      )}

      {/* Empty state */}
      {steps.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
          }}
        >
          <RadioButtonUncheckedIcon sx={{ fontSize: 48, color: colors.gray[300], mb: 2 }} />
          <Typography variant="body1" sx={{ color: colors.textSecondary }}>
            No tasks assigned to you yet
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default MyTasksList;
